import React, { useEffect, useState } from 'react';
import '../styles/components/decorativeShapes.css';

const shapeOpacity = 0.8;

// Shape types and their default animation mapping
const TYPES = [
  'taegeuk',
  'pagoda',
  'gat',
  'fan',
  'lantern',
  'mountain',
  'hangeul',
  // New icon-based shapes (from public/assets/icons)
  'korean-food',
  'mugunghwa',
  'south-korea-flag',
  'temple',
  'traditional-korean-clothes',
  'drum',
];
const ANIMS_BY_TYPE = {
  taegeuk: 'rotate',
  pagoda: 'sway',
  gat: 'rotate',
  fan: 'rotate',
  lantern: 'sway',
  mountain: 'fade',
  hangeul: 'fade',
  // icon shapes
  'korean-food': 'float',
  mugunghwa: 'rotate',
  'south-korea-flag': 'sway',
  temple: 'sway',
  'traditional-korean-clothes': 'float',
  drum: 'sway',
};

// Base pixel dimensions per shape (matches CSS sizing)
const SHAPE_BASE_SIZES = {
  taegeuk: { w: 60, h: 60 },
  pagoda: { w: 80, h: 90 },
  gat: { w: 70, h: 45 },
  fan: { w: 75, h: 60 },
  lantern: { w: 50, h: 70 },
  mountain: { w: 100, h: 50 },
  hangeul: { w: 50, h: 50 },
  // Approximations for icon-based shapes
  'korean-food': { w: 56, h: 56 },
  mugunghwa: { w: 56, h: 56 },
  'south-korea-flag': { w: 70, h: 46 },
  temple: { w: 64, h: 56 },
  'traditional-korean-clothes': { w: 56, h: 56 },
  drum: { w: 56, h: 56 },
};

function generatePositions() {
  const pageHeight = Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.clientHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight
  );
  const viewportWidth = window.innerWidth || 1;
  const isMobile = viewportWidth <= 768;

  // Size grid cells from the largest shape so every cell fits any shape
  const sizes = Object.values(SHAPE_BASE_SIZES);
  const maxW = Math.max(...sizes.map(s => s.w));
  const maxH = Math.max(...sizes.map(s => s.h));

  // Use the same value for both cellW and cellH for equal gaps
  const density = 1.5;
  const maxDim = Math.max(maxW, maxH);
  const cellH = maxDim * density;

  // Fixed grid columns based on screen size rather than viewport width
  const gridCols = isMobile ? 4 : Math.min(8, Math.max(6, Math.floor(viewportWidth / 200)));
  const gridRows = Math.max(8, Math.floor(pageHeight / cellH)); // Ensure minimum 8 rows

  // Calculate shapes to fill the grid properly, ensuring good row coverage
  const minShapes = gridCols * Math.min(8, gridRows); // At least 8 rows worth
  let numShapes = Math.max(minShapes, gridCols * gridRows); // Let grid size naturally determine shape count
  const actualCols = Math.max(3, gridCols);
  const actualRows = Math.max(8, Math.ceil(numShapes / actualCols)); // Ensure minimum 8 rows
  numShapes = actualCols * actualRows;

  // Lower jitter on both to keep the grid looking uniform
  const jitterFactor = isMobile ? 0.06 : 0.08;
  const positions = [];

  for (let i = 0; i < numShapes; i++) {
    const col = i % actualCols;
    const row = Math.floor(i / actualCols);

    const baseH = ((col + 0.5) / actualCols) * 100;
    const baseV = ((row + 0.5) / actualRows) * 100;

    const offsetH = (Math.random() - 0.5) * (100 / actualCols) * jitterFactor;
    const offsetV = (Math.random() - 0.5) * (100 / actualRows) * jitterFactor;

    positions.push({
      h: Math.max(-5, Math.min(105, baseH + offsetH)),
      v: Math.max(0, Math.min(105, baseV + offsetV)),
      r: Math.floor((Math.random() - 0.5) * 30), // -15deg to 15deg
    });
  }
  return { positions, numShapes };
}

function buildShapes() {
  const { positions, numShapes } = generatePositions();
  const shapes = [];

  for (let i = 0; i < numShapes; i++) {
    const type = TYPES[i % TYPES.length];
    const p = positions[i];
    const anim = ANIMS_BY_TYPE[type] || 'float';
    const dir = Math.random() < 0.5 ? 'normal' : 'reverse';
    const dur = `${Math.floor(18 + Math.random() * 20)}s`;
    const opacity = (0.4 + Math.random() * 0.4).toFixed(2); // goes from 0.4 to 0.8 

    shapes.push({
      type,
      style: {
        '--top': `${p.v}%`,
        '--left': `${p.h}%`,
        '--rot': `${p.r}deg`,
        '--anim': anim,
        '--dir': dir,
        '--dur': dur,
        '--opacity': opacity,
      },
    });
  }

  return shapes;
}

const ICON_TYPES = new Set([
  'korean-food',
  'mugunghwa',
  'south-korea-flag',
  'temple',
  'traditional-korean-clothes',
  'drum',
]);

function ShapeSVG({ type, style }) {
  if (ICON_TYPES.has(type)) {
  const iconUrl = `${process.env.PUBLIC_URL}/assets/icons/${type}.svg`;
  const iconStyle = { ...style, "--icon": `url('${iconUrl}')` };
    return (
      <div
        className="korean-element icon"
        data-shape={type}
        aria-label={type}
        style={iconStyle}
      />
    );
  }
  switch (type) {
    case 'taegeuk':
      return (
        <svg className="korean-element" data-shape="taegeuk" viewBox="0 0 100 100" style={style} xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="2" opacity={shapeOpacity} />
          <path d="M 50 5 A 45 45 0 0 1 50 95 A 22.5 22.5 0 0 1 50 50 A 22.5 22.5 0 0 0 50 5" fill="currentColor" opacity={shapeOpacity} />
          <path d="M 50 5 A 45 45 0 0 0 50 95 A 22.5 22.5 0 0 0 50 50 A 22.5 22.5 0 0 1 50 5" fill="none" stroke="currentColor" strokeWidth="1" opacity={shapeOpacity} />
        </svg>
      );
    case 'pagoda':
      return (
        <svg className="korean-element" data-shape="pagoda" viewBox="0 0 100 100" style={style} xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="85" width="60" height="10" fill="currentColor" opacity={shapeOpacity} />
          <rect x="28" y="55" width="6" height="30" fill="currentColor" opacity={shapeOpacity} />
          <rect x="66" y="55" width="6" height="30" fill="currentColor" opacity={shapeOpacity} />
          <rect x="47" y="55" width="6" height="30" fill="currentColor" opacity={shapeOpacity} />
          <path d="M15 60 Q50 30 85 60 Q80 65 20 65 Q15 60 15 60" fill="currentColor" opacity={shapeOpacity} />
          <path d="M25 45 Q50 20 75 45 Q70 50 30 50 Q25 45 25 45" fill="currentColor" opacity={shapeOpacity} />
          <path d="M35 32 Q50 10 65 32 Q62 36 38 36 Q35 32 35 32" fill="currentColor" opacity={shapeOpacity} />
          <ellipse cx="50" cy="28" rx="5" ry="2" fill="currentColor" opacity={shapeOpacity} />
          <rect x="48" y="10" width="4" height="18" fill="currentColor" opacity={shapeOpacity} />
        </svg>
      );
    case 'gat':
      return (
        <svg className="korean-element" data-shape="gat" viewBox="0 0 70 70" style={style} xmlns="http://www.w3.org/2000/svg">
          <ellipse cx="35" cy="55" rx="30" ry="7" fill="currentColor" opacity={shapeOpacity} />
          <rect x="25" y="20" width="20" height="30" rx="8" fill="currentColor" opacity={shapeOpacity} />
          <ellipse cx="35" cy="20" rx="10" ry="5" fill="currentColor" opacity={shapeOpacity} />
          <ellipse cx="35" cy="50" rx="13" ry="3" fill="none" stroke="currentColor" strokeWidth="1.5" opacity={shapeOpacity} />
        </svg>
      );
    case 'fan':
      return (
        <svg className="korean-element" data-shape="fan" viewBox="0 0 120 100" style={style} xmlns="http://www.w3.org/2000/svg">
          <path d="M10 30 Q60 -10 110 30 Q60 90 10 30" fill="currentColor" opacity={shapeOpacity} />
          <path d="M60 90 L10 30 M60 90 L22 18 M60 90 L34 8 M60 90 L46 2 M60 90 L60 0 M60 90 L74 2 M60 90 L86 8 M60 90 L98 18 M60 90 L110 30" stroke="currentColor" strokeWidth="1.2" opacity={shapeOpacity} />
          <path d="M10 30 Q60 -10 110 30" stroke="currentColor" strokeWidth="2.2" fill="none" opacity={shapeOpacity} />
          <rect x="56" y="90" width="8" height="12" rx="3" fill="currentColor" opacity={shapeOpacity} />
          <circle cx="35" cy="15" r="2" fill="currentColor" opacity={shapeOpacity} />
          <circle cx="60" cy="5" r="2" fill="currentColor" opacity={shapeOpacity} />
          <circle cx="85" cy="15" r="2" fill="currentColor" opacity={shapeOpacity} />
        </svg>
      );
    case 'lantern':
      return (
        <svg className="korean-element" data-shape="lantern" viewBox="0 0 50 70" style={style} xmlns="http://www.w3.org/2000/svg">
          <line x1="25" y1="5" x2="25" y2="15" stroke="currentColor" strokeWidth="1.5" opacity={shapeOpacity} />
          <rect x="15" y="15" width="20" height="18" rx="10" fill="#d32f2f" stroke="currentColor" strokeWidth="1.2" opacity={shapeOpacity} />
          <rect x="15" y="33" width="20" height="22" rx="10" fill="#1976d2" stroke="currentColor" strokeWidth="1.2" opacity={shapeOpacity} />
          <rect x="15" y="31" width="20" height="4" rx="2" fill="#fff" stroke="currentColor" strokeWidth="0.8" opacity={shapeOpacity} />
          <line x1="25" y1="55" x2="25" y2="65" stroke="#d32f2f" strokeWidth="2" opacity={shapeOpacity} />
          <circle cx="25" cy="65" r="2" fill="#d32f2f" opacity={shapeOpacity} />
        </svg>
      );
    case 'mountain':
      return (
        <svg className="korean-element" data-shape="mountain" viewBox="0 0 100 50" style={style} xmlns="http://www.w3.org/2000/svg">
          {/* Peaks */}
          <path d="M8 44 L28 18 L48 44 Z" fill="currentColor" opacity={shapeOpacity} />
          <path d="M30 44 L55 12 L78 44 Z" fill="currentColor" opacity={shapeOpacity} />
          <path d="M62 44 L74 25 L92 44 Z" fill="currentColor" opacity={shapeOpacity} />
          {/* Ridge lines */}
          <path d="M28 18 L36 28 L48 44" stroke="currentColor" strokeWidth="1.2" fill="none" opacity={shapeOpacity} />
          <path d="M55 12 L63 24 L78 44" stroke="currentColor" strokeWidth="1.2" fill="none" opacity={shapeOpacity} />
          <path d="M74 25 L83 36 L92 44" stroke="currentColor" strokeWidth="1.2" fill="none" opacity={shapeOpacity} />
          {/* Ground line */}
          <line x1="5" y1="45" x2="95" y2="45" stroke="currentColor" strokeWidth="1" opacity={shapeOpacity} />
        </svg>
      );
    case 'hangeul':
    default:
      return (
        <span className="korean-element hangeul-text" data-shape="hangeul" style={style}>환영</span>
      );
  }
}

const DecorativeShapes = ({ style, className }) => {
  const [shapes, setShapes] = useState([]);

  useEffect(() => {
    const refresh = () => setShapes(buildShapes());
    refresh();

    const handleResize = () => {
      clearTimeout(window.__dsTO);
      window.__dsTO = setTimeout(refresh, 120);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={`korean-decorative-container${className ? ` ${className}` : ''}`} style={style}>
      {shapes.map((s, idx) => (
        <ShapeSVG key={idx} type={s.type} style={s.style} />
      ))}
    </div>
  );
};

export default DecorativeShapes;
